import { Form, Button } from 'react-bootstrap';
import { ExclamationTriangle, InfoCircle } from 'react-bootstrap-icons';
import { ReactNode, useState } from 'react';
import './ConfigField.css';

interface ConfigFieldProps {
  /** The config key. It names the control, so a renamed label never changes the control's id. */
  fieldKey: string;
  label: string;
  value: string | boolean | number;
  defaultValue: string | boolean | number;
  /** The default in words for the label line. Without it, the default option's label (or the value) is used. */
  defaultLabel?: string;
  /**
   * Update page: what a new client starts with, when it differs from the
   * default shown ("new clients on"). Both values are then normal for an
   * existing client, so the field is never marked "Not default" and has no
   * reset.
   */
  newClientDefault?: string | boolean;
  /** False for a value that is not a choice here (locked on Update): no default on the label line. */
  showDefault?: boolean;
  onChange: (value: any) => void;
  type?: 'text' | 'switch' | 'select' | 'number' | 'tri-state';
  options?: { label: string; value: string; disabled?: boolean }[];
  placeholder?: string;
  disabled?: boolean;
  /** Info note rendered under the field while `disabled`, explaining why it is greyed out. */
  disabledNote?: string;
  helpText?: string;
  children?: ReactNode;
  /** Warning note rendered whenever the field holds a non-default value. */
  warnWhenSet?: string;
  /**
   * FEAT-239 — key-removal affordance (Update mode). `removable` shows a
   * "Remove from config" link when the key physically exists in the record;
   * `removed` renders the pending-removal state with an Undo.
   */
  removable?: boolean;
  removed?: boolean;
  onRemove?: () => void;
  onUndoRemove?: () => void;
  /** Human description of what the deployment falls back to after removal. */
  removeFallbackNote?: string;
}

/** Help longer than this folds to two lines, with a link to show the rest. */
const LONG_HELP = 180;

/** The default as the label line shows it: "on", "off", an option's label, or nothing for an empty text default. */
function defaultWords(
  type: ConfigFieldProps['type'],
  defaultValue: ConfigFieldProps['defaultValue'],
  defaultLabel: string | undefined,
  options: { label: string; value: string }[]
): string | null {
  if (defaultLabel) return defaultLabel;
  if (type === 'switch') return defaultValue ? 'on' : 'off';
  if (type === 'select' || type === 'tri-state') {
    return options.find((o) => o.value === String(defaultValue))?.label ?? null;
  }
  return String(defaultValue) === '' ? null : String(defaultValue);
}

export function ConfigField({
  fieldKey,
  label,
  value,
  defaultValue,
  defaultLabel,
  newClientDefault,
  showDefault = true,
  onChange,
  type = 'text',
  options = [],
  placeholder,
  disabled = false,
  disabledNote,
  helpText,
  children,
  warnWhenSet,
  removable = false,
  removed = false,
  onRemove,
  onUndoRemove,
  removeFallbackNote,
}: ConfigFieldProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  const isDefault = value === defaultValue || newClientDefault !== undefined;
  const showReset = !isDefault && !disabled && !removed;
  const fieldId = `config-${fieldKey}`;
  const defaultText = showDefault ? defaultWords(type, defaultValue, defaultLabel, options) : null;
  const longHelp = !!helpText && helpText.length > LONG_HELP;

  if (removed) {
    return (
      <Form.Group className="config-field config-field--removed" data-config-key={fieldKey}>
        <div className="config-field__head">
          <Form.Label className="config-field__label">{label}</Form.Label>
          <Button variant="link" size="sm" className="config-field__link" onClick={onUndoRemove}>
            Undo remove
          </Button>
        </div>
        <div className="config-field__note config-field__note--warning">
          <ExclamationTriangle aria-hidden="true" />
          <span>
            This setting leaves the config when you save.
            {removeFallbackNote ? <> {removeFallbackNote}</> : null}
          </span>
        </div>
      </Form.Group>
    );
  }

  const classes = [
    'config-field',
    type === 'switch' ? 'config-field--switch' : '',
    isDefault ? '' : 'config-field--changed',
    disabled ? 'config-field--disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Form.Group className={classes} data-config-key={fieldKey}>
      <div className="config-field__head">
        <div className="config-field__title">
          <Form.Label htmlFor={fieldId} className="config-field__label">
            {label}
          </Form.Label>
          {defaultText && <span className="config-field__default">default {defaultText}</span>}
          {newClientDefault !== undefined && (
            <span className="config-field__default">
              · new clients {defaultWords(type, newClientDefault, undefined, options) ?? String(newClientDefault)}
            </span>
          )}
          {!isDefault && <span className="config-field__tag">Not default</span>}
        </div>
        {type === 'switch' && (
          <Form.Check
            id={fieldId}
            type="switch"
            label={value ? 'On' : 'Off'}
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="config-field__switch"
          />
        )}
      </div>

      {(type === 'text' || type === 'number') && (
        <Form.Control
          id={fieldId}
          type={type === 'number' ? 'number' : 'text'}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="config-field__control"
        />
      )}

      {(type === 'select' || type === 'tri-state') && (
        <Form.Select
          id={fieldId}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="config-field__control"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )}

      {children}

      {disabled && disabledNote && (
        <div className="config-field__note config-field__note--info">
          <InfoCircle aria-hidden="true" />
          <span>{disabledNote}</span>
        </div>
      )}

      {helpText && (
        <Form.Text className={`config-field__help${longHelp && !helpOpen ? ' config-field__help--folded' : ''}`}>
          {helpText}
        </Form.Text>
      )}

      {warnWhenSet && !isDefault && (
        <div className="config-field__note config-field__note--warning">
          <ExclamationTriangle aria-hidden="true" />
          <span>
            <strong>Warning:</strong> {warnWhenSet}
          </span>
        </div>
      )}

      {(longHelp || showReset || (removable && !disabled)) && (
        <div className="config-field__links">
          {longHelp && (
            <Button
              variant="link"
              size="sm"
              className="config-field__link"
              onClick={() => setHelpOpen((open) => !open)}
              aria-expanded={helpOpen}
            >
              {helpOpen ? 'Less' : 'More'}
            </Button>
          )}
          {showReset && (
            <Button variant="link" size="sm" className="config-field__link" onClick={() => onChange(defaultValue)}>
              Reset to default
            </Button>
          )}
          {removable && !disabled && (
            <Button
              variant="link"
              size="sm"
              className="config-field__link config-field__link--danger"
              onClick={onRemove}
              title="Take this setting out of the stored config. The client then gets the default."
            >
              Remove from config
            </Button>
          )}
        </div>
      )}
    </Form.Group>
  );
}

export default ConfigField;
